import { useCallback } from "react";

import { useAppDispatch, useAppStore } from "app/redux-store-interactions";
import { bottomToolbarActions, selectActiveTab, selectIsExpanded } from "components/toolbars/mobile/slice";
import { highlightActions, useDispatchHighlighted, useLazyHighlighted } from "contexts/highlighted";
import { coordinatesActions } from "features/coordinates";
import { measureActions } from "features/measure/measureSlice";
import { clippingOutlineLaserActions } from "features/outlineLaser";
import { pointLineActions } from "features/pointLine";
import { Picker, renderActions, selectMainObject, selectMainObjects, selectPicker } from "features/render";
import { discardPendingOptimisticPaint, getPendingOptimisticPaint } from "features/render/hooks/sharedOptimisticPaint";
import { abortSharedSelection } from "features/render/hooks/sharedSelectionAbort";
import { undoRedoActions } from "features/undoRedo";
import { selectIsProcoreDesign } from "slices/explorer";
import { ProcoreToolMode, procoreUxActions, selectActiveToolMode } from "slices/procoreUx";
import { getToolModeForPicker } from "slices/procoreUx/utils";
import { restoreUndoIds, snapshotUndoIds } from "utils/undoIds";

export function useEnterToolMode() {
    const dispatch = useAppDispatch();
    const store = useAppStore();
    const highlighted = useLazyHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();

    return useCallback(
        (params: { mode: ProcoreToolMode } | { picker: Picker }) => {
            const state = store.getState();
            const currentMode = selectActiveToolMode(state);
            const currentPicker = selectPicker(state);
            const isProcoreDesign = selectIsProcoreDesign(state);
            const isBottomToolbarExpandedBefore = selectIsExpanded(state);
            const activeTab = selectActiveTab(state);

            let picker = currentPicker;
            let mode = currentMode;
            if ("mode" in params) {
                mode = params.mode;
                const currentPickerBelongsToMode = getToolModeForPicker(currentPicker) === mode;
                if (!currentPickerBelongsToMode) {
                    picker = Picker.Object;
                }
            } else {
                picker = params.picker;
                mode = getToolModeForPicker(picker) ?? currentMode;
            }

            if (mode === currentMode && picker === currentPicker) {
                return;
            }

            const shouldSnapshot = currentMode === ProcoreToolMode.Default && mode !== currentMode && isProcoreDesign;

            // The object-to-object tool highlights its picks in the selection colour, so a selection
            // left standing would read as objects already picked. Cleared as part of entering, so
            // undoing the entry brings it back. While a click's optimistic paint is on screen, what is
            // highlighted is that temporary fragment, so undo goes back to the selection from before
            // it - as a background click does.
            const pendingPaint = getPendingOptimisticPaint();
            const previousSelectionIds = pendingPaint?.previousHighlightIds ?? highlighted.current.idArr;
            const shouldClearSelection =
                picker === Picker.ObjectMeasurement &&
                currentPicker !== Picker.ObjectMeasurement &&
                (previousSelectionIds.length > 0 || highlighted.current.idArr.length > 0);
            const previousSelection = shouldClearSelection ? snapshotUndoIds(previousSelectionIds) : undefined;
            const previousMainObject = pendingPaint ? pendingPaint.previousMainObject : selectMainObject(state);
            const previousMainObjects = pendingPaint ? pendingPaint.previousMainObjects : selectMainObjects(state);

            const apply = () => {
                dispatch(procoreUxActions.setActiveToolMode(mode));
                dispatch(renderActions.setPicker(picker));
                if (!isBottomToolbarExpandedBefore) {
                    dispatch(bottomToolbarActions.expand());
                }
                dispatch(bottomToolbarActions.setActiveTab("tools"));
                if (shouldSnapshot) {
                    // Snapshot the current state once going into a tool mode
                    dispatch(pointLineActions.captureEditSessionStartSnapshot());
                    dispatch(clippingOutlineLaserActions.captureEditSessionStartSnapshot());
                    dispatch(coordinatesActions.captureEditSessionStartSnapshot());
                    dispatch(measureActions.captureObjectMeasureEditSessionStartSnapshot());
                    dispatch(renderActions.captureClippingEditSessionStartSnapshot());
                }
                if (shouldClearSelection) {
                    // A selection still resolving would otherwise paint itself, or the one it
                    // replaced, back over the clear.
                    abortSharedSelection();
                    discardPendingOptimisticPaint();
                    dispatch(renderActions.setMainObject(undefined));
                    dispatch(renderActions.clearMainObjects());
                    dispatchHighlighted(highlightActions.setIds([]));
                }
            };

            apply();

            dispatch(
                undoRedoActions.push({
                    description: "Enter tool mode",
                    undoCallback: () => {
                        dispatch(procoreUxActions.setActiveToolMode(currentMode));
                        dispatch(renderActions.setPicker(currentPicker));
                        if (!isBottomToolbarExpandedBefore) {
                            dispatch(bottomToolbarActions.collapse());
                        }
                        dispatch(bottomToolbarActions.setActiveTab(activeTab));
                        if (shouldSnapshot) {
                            dispatch(pointLineActions.clearEditSessionStartSnapshot());
                            dispatch(clippingOutlineLaserActions.clearEditSessionStartSnapshot());
                            dispatch(coordinatesActions.clearEditSessionStartSnapshot());
                            dispatch(measureActions.clearObjectMeasureEditSessionStartSnapshot());
                            dispatch(renderActions.clearClippingEditSessionStartSnapshot());
                        }
                        if (previousSelection) {
                            dispatchHighlighted(highlightActions.setIds(restoreUndoIds(previousSelection)));
                            if (previousMainObject !== undefined) {
                                dispatch(renderActions.setMainObject(previousMainObject));
                            }
                            dispatch(renderActions.setMainObjects(previousMainObjects));
                        }
                    },
                    redoCallback: apply,
                }),
            );
        },
        [dispatch, store, highlighted, dispatchHighlighted],
    );
}
